import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth'
import { QB_BLOCKER_EN, rupees, usePatientMatches, useQuickBill, type QbGender, type QbMode } from '../quickBill'
import '../quickbill.css'

/* The desk's home page: bill a walk-in in one go. A name is enough; gender
 * and age if they have them. Pick the doctor and/or the tests, take the money,
 * print the receipt. Billed tests land on today's lab sheet. */

const GENDERS: [QbGender, string][] = [['female', 'Female'], ['male', 'Male'], ['other', 'Other']]
const MODES: [QbMode, string][] = [['cash', 'Cash'], ['upi', 'UPI'], ['card', 'Card']]

export default function QuickBill() {
  const { user } = useAuth()
  const qb = useQuickBill()
  const [testQuery, setTestQuery] = useState('')
  const matches = usePatientMatches(qb.name, !qb.existing)
  const shownTests = useMemo(() => {
    const q = testQuery.trim().toLowerCase()
    const list = q ? qb.tests.filter((t) => `${t.name} ${t.category ?? ''}`.toLowerCase().includes(q)) : qb.tests
    return list.slice(0, q ? 24 : 12)
  }, [qb.tests, testQuery])
  const chosen = new Set(qb.lines.map((l) => l.refId))

  if (qb.result) {
    const r = qb.result
    const paid = r.totals.settlement === 'paid'
    return (
      <>
        <div className="page-head qb-head">
          <div><h2>{paid ? 'Paid' : 'Bill saved'}</h2><p>{paid ? 'Hand over the receipt.' : 'Saved unpaid. Collect it from Bills when the patient pays.'}</p></div>
          <div className="qb-head-actions">
            <button type="button" className="secondary" onClick={() => window.print()}>Print {paid ? 'receipt' : 'bill'}</button>
            <button type="button" onClick={qb.reset}>New bill</button>
          </div>
        </div>
        <div className="qb-receipt">
          <div className="qb-receipt-top">
            <div><b>{user?.clinicName ?? 'Clinic'}</b><span>{paid ? 'Receipt' : 'Bill'}</span></div>
            <div className="qb-receipt-nos">
              <span>Bill {r.invoice.invoiceNo}</span>
              {r.receipt && <span>Receipt {r.receipt.receiptNo}</span>}
              <span>{new Date(r.invoice.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
            </div>
          </div>
          <div className="qb-receipt-patient"><b>{r.patient.fullName}</b><span>{r.patient.patientCode}</span></div>
          <table className="qb-receipt-lines">
            <tbody>
              {r.items.map((i) => <tr key={i.id}><td>{i.description}</td><td className="num">{rupees(i.lineTotal)}</td></tr>)}
            </tbody>
            <tfoot>
              <tr><td>Total</td><td className="num">{rupees(r.invoice.total)}</td></tr>
              {r.receipt && <tr className="qb-sub"><td>Paid by {r.receipt.mode.toUpperCase()}</td><td className="num">{rupees(r.receipt.amountPaid)}</td></tr>}
              {r.receipt?.cashReceived && <tr className="qb-sub"><td>Cash received · change</td><td className="num">{rupees(r.receipt.cashReceived)} · {rupees(r.receipt.changeGiven)}</td></tr>}
              {!paid && <tr className="qb-sub"><td>Due</td><td className="num">{rupees(r.totals.outstanding)}</td></tr>}
            </tfoot>
          </table>
          {r.labBookingIds.length > 0 && <p className="qb-receipt-note">{r.labBookingIds.length === 1 ? 'The test is' : `${r.labBookingIds.length} tests are`} on today’s lab sheet.</p>}
        </div>
      </>
    )
  }

  return (
    <>
      <div className="page-head qb-head">
        <div><h2>New bill</h2><p>A name is enough. Pick what they came for and take the money.</p></div>
        <div className="qb-head-actions"><Link to="/billing" className="qb-link">All bills</Link></div>
      </div>
      {qb.loadError && <div className="msg error">{qb.loadError}</div>}

      <div className="qb">
        <div className="qb-left">
          <section className="panel">
            <h3>Patient</h3>
            {qb.existing ? (
              <div className="qb-picked">
                <div><b>{qb.existing.fullName}</b><span>{qb.existing.patientCode}{qb.existing.mobile ? ` · ${qb.existing.mobile}` : ''}</span></div>
                <button type="button" className="link" onClick={() => qb.setExisting(null)}>Change</button>
              </div>
            ) : (
              <div className="qb-patient">
                <div className="field qb-name">
                  <label htmlFor="qb-name">Name <span className="qb-req">required</span></label>
                  <input id="qb-name" autoFocus autoComplete="off" value={qb.name} onChange={(e) => qb.setName(e.target.value)} placeholder="Patient’s name" />
                  {matches.length > 0 && (
                    <div className="qb-matches" role="listbox" aria-label="Existing patients">
                      <span>Already registered?</span>
                      {matches.map((m) => (
                        <button key={m.id} type="button" className="qb-match" onClick={() => { qb.setExisting(m); qb.setName('') }}>
                          <b>{m.fullName}</b><small>{m.patientCode}{m.mobile ? ` · ${m.mobile}` : ''}</small>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="field">
                  <label>Gender <span className="qb-opt">optional</span></label>
                  <div className="qb-seg" role="group" aria-label="Gender">
                    {GENDERS.map(([g, l]) => (
                      <button key={g} type="button" aria-pressed={qb.gender === g} className={qb.gender === g ? 'on' : ''}
                        onClick={() => qb.setGender(qb.gender === g ? '' : g)}>{l}</button>
                    ))}
                  </div>
                </div>
                <div className="field qb-age">
                  <label htmlFor="qb-age">Age <span className="qb-opt">optional</span></label>
                  <input id="qb-age" type="number" min={0} max={130} inputMode="numeric" value={qb.age} onChange={(e) => qb.setAge(e.target.value)} placeholder="Years" />
                </div>
              </div>
            )}
          </section>

          <section className="panel">
            <h3>Consultation</h3>
            <div className="qb-chips">
              {qb.doctors.map((d) => (
                <button key={d.id} type="button" className={`qb-chip${chosen.has(d.id) ? ' on' : ''}`} onClick={() => qb.addDoctor(d)}>
                  <b>{d.fullName}</b><small>{d.specialization ?? 'Consultation'} · {rupees(d.consultationFee)}</small>
                </button>
              ))}
              {qb.doctors.length === 0 && !qb.loadError && <span className="subtle">No doctors set up yet.</span>}
            </div>
          </section>

          <section className="panel">
            <h3>Lab tests</h3>
            <input aria-label="Find a test" placeholder="Find a test" value={testQuery} onChange={(e) => setTestQuery(e.target.value)} />
            <div className="qb-chips qb-tests">
              {shownTests.map((t) => (
                <button key={t.id} type="button" className={`qb-chip${chosen.has(t.id) ? ' on' : ''}`} onClick={() => qb.addTest(t)} disabled={chosen.has(t.id)}>
                  <b>{t.name}</b><small>{t.typicalCost == null ? 'No price set' : rupees(t.typicalCost)}</small>
                </button>
              ))}
              {shownTests.length === 0 && <span className="subtle">No test matches.</span>}
            </div>
            <button type="button" className="link qb-other" onClick={qb.addOther}>+ Another charge</button>
          </section>
        </div>

        <aside className="qb-right">
          <section className="qb-bill">
            <h3>Bill</h3>
            {qb.lines.length === 0 && <p className="qb-empty">Nothing added yet. Tap a doctor or a test.</p>}
            <ul className="qb-lines">
              {qb.lines.map((l) => (
                <li key={l.key}>
                  {l.kind === 'other'
                    ? <input aria-label="What is this charge" className="qb-line-label" placeholder="What is it for" value={l.label} onChange={(e) => qb.updateLine(l.key, { label: e.target.value })} />
                    : <span className="qb-line-label">{l.label}</span>}
                  <span className="qb-price"><span>₹</span><input aria-label={`Price for ${l.label || 'this line'}`} inputMode="decimal" value={l.price} onChange={(e) => qb.updateLine(l.key, { price: e.target.value })} /></span>
                  <button type="button" className="qb-x" aria-label={`Remove ${l.label || 'line'}`} onClick={() => qb.removeLine(l.key)}>×</button>
                </li>
              ))}
            </ul>
            <div className="qb-total"><span>Total</span><b>{rupees(qb.total)}</b></div>

            <div className="qb-seg qb-modes" role="group" aria-label="Payment">
              {MODES.map(([m, l]) => (
                <button key={m} type="button" aria-pressed={qb.mode === m} className={qb.mode === m ? 'on' : ''} onClick={() => qb.setMode(m)}>{l}</button>
              ))}
            </div>
            {qb.mode === 'cash' ? (
              <div className="field">
                <label htmlFor="qb-cash">Cash received <span className="qb-opt">optional</span></label>
                <input id="qb-cash" inputMode="decimal" value={qb.cash} onChange={(e) => qb.setCash(e.target.value)} placeholder={String(qb.total || '')} />
                {qb.change != null && qb.change >= 0 && <span className="qb-change">Change to give: <b>{rupees(qb.change)}</b></span>}
              </div>
            ) : (
              <div className="field">
                <label htmlFor="qb-ref">{qb.mode === 'upi' ? 'UPI reference' : 'Card slip number'} <span className="qb-opt">optional</span></label>
                <input id="qb-ref" value={qb.reference} onChange={(e) => qb.setReference(e.target.value)} />
              </div>
            )}

            {qb.error && <div className="msg error">{qb.error}</div>}
            <button type="button" className="qb-collect" disabled={qb.busy || !!qb.blocker} onClick={() => void qb.submit(true)}>
              {qb.busy ? 'Saving…' : qb.total > 0 ? `Collect ${rupees(qb.total)}` : 'Save bill'}
            </button>
            <button type="button" className="secondary qb-later" disabled={qb.busy || !!qb.blocker} onClick={() => void qb.submit(false)}>Save unpaid</button>
            {qb.blocker && qb.lines.length + qb.name.length > 0 && <p className="qb-hint">{QB_BLOCKER_EN[qb.blocker]}</p>}
          </section>
        </aside>
      </div>
    </>
  )
}
